<template>
  <div>
    <UtilTabs
      @switchTab="switchTab"
      v-model="activeTab"
      :tabs="compTabs"
      class="mb-2 px-2"
      targetClass="bg-background"
    />

    <UtilSearch
      :key="`${activeTab}-listings`"
      @foundResults="$emit('loaded')"
      @noResults="noResults()"
      :searchQuery="searchQuery"
      class="px-2"
    >
      <template #loading>
        <div v-for="i in 7" :key="i">
          <UtilPlaceholderHitCardList class="mb-2" />
        </div>
      </template>

      <template #default="{ listings }">
        <template v-if="!listings.length">
          <UtilEmpty :icon="faSearchMinus" :title="emtpyMessage" class="py-8 text-xs" />
        </template>

        <UtilHitCard
          v-for="relatedListing in listings"
          :key="`${activeTab}-${relatedListing.listingRef}`"
          @click="relatedClick()"
          type="listing"
          layout="list"
          :listing="relatedListing"
          :to="`/view/listing/${relatedListing.url}`"
          :canWatch="false"
          shadow
          class="mb-2 related-listing"
        />

        <WidgetAdvert
          v-if="listings.length > 0 && $breakpoint.smAndDown"
          key="advert-related-1"
          shape="banner"
          :listingRef="listing.listingRef"
          :advertIndex="1"
        />
      </template>
    </UtilSearch>
  </div>
</template>

<script>
  import {faSearchMinus} from '@fortawesome/pro-regular-svg-icons'
  import UtilHitCard from '@/components/utils/UtilHitCard'
  import UtilPlaceholderHitCardList from '@/components/utils/placeholders/UtilPlaceholderHitCardList'
  import UtilSearch from '@/components/utils/search/UtilSearch'
  import UtilEmpty from '@/components/utils/UtilEmpty'
  import UtilTabs from '@/components/utils/UtilTabs'
  import WidgetAdvert from '@/components/widgets/WidgetAdvert'

  export default {
  name: 'ListingRelated',
  components: {
    UtilHitCard,
    UtilPlaceholderHitCardList,
    UtilSearch,
    UtilEmpty,
    UtilTabs,
    WidgetAdvert
  },
  props: {
    isLoading: Boolean,
    listing: {
      type: Object,
      required: true
    },
    listingIs: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: 'related',
      searchQuery: null,
      hasNoResultsSwitchedAlready: false,
      emtpyMessage: this.$t('listing.noRelatedResults'),
      faSearchMinus
    }
  },
  watch: {
    // Did we switch listing? Let's re-fetch
    listing() {
      this.search()
    }
  },
  created() {
    this.switchTab('related')
  },
  computed: {
    compTabs() {
      return [
        {
          text: this.$t('listing.related'),
          value: 'related'
        },
        {
          text: this.$t('listing.moreNearby'),
          value: 'moreNearby'
        }
      ]
    }
  },
  methods: {
    switchTab(tab, perfomedByUser = false) {
      this.activeTab = tab

      this.search()

      if (perfomedByUser) {
        this.$analytics.addEvent({
          category: 'listing',
          action: 'Switched related tab',
          label: tab
        })
      }
    },

    search() {
      if (!this.listing.commoditySlug) {
        return false
      }

      const data = {
        related: {
          searchQuery: {
            commodity: this.listing.commoditySlug,
            statuses: ['L'],
            exclude: {
              listingRefs: [this.listing.listingRef]
            },
            pageLength: 6
          },
          emtpyMessage: this.$t('listing.noRelatedResults')
        },
        moreNearby: {
          searchQuery: {
            commodity: this.listing.commoditySlug,
            statuses: ['L'],
            locationCode: this.listing && this.listing.location[0].code,
            sortBy: 'NEAREST',
            exclude: {
              listingRefs: [this.listing.listingRef]
            },
            pageLength: 6
          },
          emtpyMessage: this.$t('listing.noMoreNearby')
        }
      }

      // Portal auction partner listings only show listings from their portals
      if (this.listingIs.portalAuctionPartner) {
        data.related.searchQuery.portals = this.listing.portals.map(portal => portal.portalRef)
        data.moreNearby.searchQuery.portals = this.listing.portals.map(portal => portal.portalRef)
      } else {
        // Add to related search query
        if (this.listingIs.livestock && this.activeTab === 'related') {
          let categories = []
          let meta = []

          this.listing.categories.forEach(breed => {
            if (breed.ancestors.length > 1) {
              categories.push(breed.categorySlug)
            }
          })

          this.listing.meta.sexes.forEach(sex => {
            meta.push({
              fieldName: 'Sexes',
              logic: 'CONTAINS',
              value: sex
            })
          })

          this.listing.meta.types.forEach(type => {
            meta.push({
              fieldName: 'Types',
              logic: 'CONTAINS',
              value: type
            })
          })

          data.related = {
            ...data.related,
            searchQuery: {
              ...data.related.searchQuery,
              meta,
              categories
            }
          }
        }
      }

      this.searchQuery = data[this.activeTab].searchQuery
      this.emtpyMessage = data[this.activeTab].emtpyMessage
    },

    relatedClick() {
      this.$analytics.addEvent({
        category: 'listing',
        action: 'Clicked a related listing',
        label: this.activeTab
      })
    },

    noResults() {
      // No results, switch to the other tab
      // Only switch once though (we start on related)
      if (this.activeTab === 'related' && !this.hasNoResultsSwitchedAlready) {
        this.switchTab('moreNearby')
        this.hasNoResultsSwitchedAlready = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tab {
  @apply py-2 px-4 inline-block;
  @apply bg-background text-primary font-semibold cursor-pointer;

  &:hover {
    @apply text-gray-darker;
  }
}

.tab.active {
  @apply border-l border-t border-r border-gray rounded-t;
}
</style>
